import { Box, Typography } from "@mui/material"
import { faClock, faUserTie, faBuilding, faLocationDot } from "@fortawesome/pro-regular-svg-icons"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { MeetingOrderOrganizer } from "../../meetings/meetingTypes"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { getHostOrganizer, getHostOrganizerCompanyName, getHostOrganizerName } from "../meetigs/helpers"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useState } from "react"
import { Stack } from "@mui/system"

const useStyles = makeStyles()(() => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
    },
    detailRow: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    icon: {
        color: tailwindPalette.slate[400],
        width: "1rem",
    },
    infoButton: {
        cursor: "pointer",
        marginLeft: ".5rem",
        color: "#64748B",
        ":hover": {
            color: "#94A3B8",
        },
    },
    userDialog: {
        padding: "0 2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
}))

type MeetingDetailsProps = {
    time?: string
    location?: string
    organizers?: Array<MeetingOrderOrganizer>
}

export function MeetingDetails({ time, location, organizers }: MeetingDetailsProps) {
    const { classes } = useStyles()
    const [isUserDetailsDialog, openDialog] = useState(false)

    const hostOrganizer = getHostOrganizer(
        organizers?.map((organizer) => ({
            type: organizer.organizerType,
            ...organizer,
        })),
    )

    return (
        <Box className={classes.container}>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faUserTie} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-host">
                        <Typography component="span" variant="subtitle1">
                            Host:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {getHostOrganizerName(
                        organizers?.map((organizer) => ({
                            name: organizer.name,
                            type: organizer.organizerType,
                            email: organizer.email,
                        })),
                    )}
                    <FontAwesomeIcon
                        onClick={(e) => {
                            openDialog(true)
                        }}
                        className={classes.infoButton}
                        icon={faInfoCircle}
                    />
                </Typography>
            </Box>
            <StandardDialog open={isUserDetailsDialog} onClose={() => openDialog(false)} maxWidthProp="sm">
                <Box className={classes.userDialog}>
                    {hostOrganizer?.name && (
                        <Stack direction="row" width="300px" justifyContent="space-between">
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-name">
                                <Typography>Name: </Typography>
                            </LocalizedStrict>
                            <Typography> {hostOrganizer?.name}</Typography>
                        </Stack>
                    )}
                    {hostOrganizer?.company?.name && (
                        <Stack direction="row" width="300px" justifyContent="space-between">
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-company-name">
                                <Typography>Company: </Typography>
                            </LocalizedStrict>
                            <Typography> {hostOrganizer?.company?.name}</Typography>
                        </Stack>
                    )}
                    {hostOrganizer?.department?.name && (
                        <Stack direction="row" width="300px" justifyContent="space-between">
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-department-name">
                                <Typography>Department: </Typography>
                            </LocalizedStrict>
                            <Typography> {hostOrganizer?.department?.name}</Typography>
                        </Stack>
                    )}
                    {hostOrganizer?.email && (
                        <Stack direction="row" width="300px" justifyContent="space-between">
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-email">
                                <Typography>E-mail: </Typography>
                            </LocalizedStrict>
                            <Typography> {hostOrganizer?.email}</Typography>
                        </Stack>
                    )}
                    {hostOrganizer?.businessPhone && (
                        <Stack direction="row" width="300px" justifyContent="space-between">
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-business-phone">
                                <Typography>Phone number: </Typography>
                            </LocalizedStrict>
                            <Typography> {hostOrganizer?.businessPhone}</Typography>
                        </Stack>
                    )}
                    {hostOrganizer?.mobilePhone && (
                        <Stack direction="row" width="300px" justifyContent="space-between">
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-mobile-phone">
                                <Typography>Mobile number: </Typography>
                            </LocalizedStrict>
                            <Typography> {hostOrganizer?.mobilePhone}</Typography>
                        </Stack>
                    )}
                </Box>
            </StandardDialog>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faBuilding} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-host-company">
                        <Typography component="span" variant="subtitle1">
                            Company:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {getHostOrganizerCompanyName(
                        organizers?.map((organizer) => ({
                            name: organizer.name,
                            type: organizer.organizerType,
                            email: organizer.email,
                            company: organizer.company,
                        })),
                    )}
                </Typography>
            </Box>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faClock} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-meeting-time">
                        <Typography component="span" variant="subtitle1">
                            Meeting time:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {time}
                </Typography>
            </Box>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faLocationDot} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-location">
                        <Typography component="span" variant="subtitle1">
                            Location:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {location}
                </Typography>
            </Box>
        </Box>
    )
}
